import { useCallback, useRef, useState } from "react";
import { FilterKey, FilterOption, FilterValues } from "../types/common/filters";
import { CountryResponse } from "../types/api/common";
import {
  ActionType,
  ButtonName,
  ContactType,
  getButtonName,
  getContactTypeName,
  getHintName,
  getTimeOfDayName,
  getUserTypeName,
  Hint,
  ScreenName,
  TimeOfDay,
  MetricsUserType,
} from "../types/api/metrics/request";
import { CitiesListRequest } from "../types/api/cities";
import { fetchCitiesList } from "../services/geo/cities";
import {
  CatalogOptionsListRequest,
  FilterValueRequest,
  FilterValueResponse,
} from "../types/api/metadata";
import {
  fetchCatalogOptions,
  fetchCatalogs,
  fetchCities,
  fetchCountries,
} from "../services/metadata";
import { fetchCategories } from "../services/categories";
import { truncateText } from "../helpers/strings";
import { CatalogResponse, CategoryResponse } from "../types/api/ads";
import {
  getScoringUserSegmentFilter,
  ScoringUserSegment,
} from "../types/api/scoring/scoring";
import { FilterGroup } from "../components/scoring/components/GroupedSelectFormModal";
import { scoringSegmentGroups } from "../components/scoring/ScoringList+Filter";
import { fetchCountriesList } from "../services/geo/countries";

interface UseFilterOptionsParams {
  values: FilterValues;
}

export const useFilterOptions = ({ values }: UseFilterOptionsParams) => {
  const [loadedOptions, setLoadedOptions] = useState<
    Record<FilterKey, FilterOption[]>
  >({} as Record<FilterKey, FilterOption[]>);
  const [loadingStates, setLoadingStates] = useState<
    Record<FilterKey, boolean>
  >({} as Record<FilterKey, boolean>);
  const [filterOffsets, setFilterOffsets] = useState<Record<FilterKey, number>>(
    {} as Record<FilterKey, number>
  );
  const [filterHasMore, setFilterHasMore] = useState<
    Record<FilterKey, boolean>
  >({} as Record<FilterKey, boolean>);
  const [filterLoadingMore, setFilterLoadingMore] = useState<
    Record<FilterKey, boolean>
  >({} as Record<FilterKey, boolean>);

  const filterOffsetsRef = useRef<Record<FilterKey, number>>(
    {} as Record<FilterKey, number>
  );

  const buttonNameOptions = Object.values(ButtonName).map((value) => ({
    value,
    label: getButtonName(value),
  }));

  const timeOfDayOptions = Object.values(TimeOfDay).map((value) => ({
    value,
    label: getTimeOfDayName(value),
  }));

  const screenOptions = Object.values(ScreenName).map((value) => ({
    value,
    label: value,
  }));

  const hintOptions = Object.values(Hint).map((value) => ({
    value,
    label: getHintName(value),
  }));

  const actionOptions = Object.values(ActionType).map((value) => ({
    value,
    label: value,
  }));

  const contactOptions = Object.values(ContactType).map((value) => ({
    value,
    label: getContactTypeName(value),
  }));

  const userOptions = Object.values(MetricsUserType).map((value) => ({
    value,
    label: getUserTypeName(value),
  }));

  const [searchQueries, setSearchQueries] = useState<Record<FilterKey, string>>(
    {} as Record<FilterKey, string>
  );

  const loadFilterOptions = useCallback(
    async (
      filterKey: FilterKey,
      loadMore: boolean = false,
      forceReload: boolean = false,
      searchQuery: string = ""
    ) => {
      console.log("loadFilterOptions CALLED with:", {
        filterKey,
        loadMore,
        forceReload,
        searchQuery,
      });
      if (!forceReload && loadedOptions[filterKey]?.length > 0 && !loadMore) {
        return;
      }

      if (loadMore && filterLoadingMore[filterKey]) {
        return;
      }

      if (!loadMore && loadingStates[filterKey]) {
        return;
      }

      const currentOffset = loadMore
        ? filterOffsetsRef.current[filterKey] || 0
        : 0;
      const limit = 20;

      if (loadMore) {
        setFilterLoadingMore((prev) => ({ ...prev, [filterKey]: true }));
      } else {
        setLoadingStates((prev) => ({ ...prev, [filterKey]: true }));
        filterOffsetsRef.current[filterKey] = 0;
      }

      try {
        let options: FilterOption[] = [];
        let hasMore = false;
        const currentSearchQuery = searchQuery;

        switch (filterKey) {
          case "countryResponseForId":
            const countriesIdParams: FilterValueRequest = {
              limit,
              offset: currentOffset,
              country: undefined,
              search: currentSearchQuery,
            };

            try {
              const result = await fetchCountriesList(countriesIdParams);
              options = result.list.map((country) => ({
                value: country,
                label: country.title,
              }));
              hasMore = result.list.length === limit;
            } catch (error) {
              console.error("Ошибка загрузки стран:", error);
              options = [];
            }
            break;
          case "cityResponseForId":
            const countryIds = values["countryResponseForId"]?.map(
              (el: CountryResponse) => el.id
            );

            const citiesParams: CitiesListRequest = {
              limit,
              offset: currentOffset,
              ordering: [],
              search: currentSearchQuery,
              country_id: countryIds || undefined,
            };

            try {
              const result = await fetchCitiesList(citiesParams);
              options = result.list.map((city) => ({
                value: city,
                label: city.title,
              }));
              hasMore = result.list.length === limit;
            } catch (error) {
              console.error("Ошибка загрузки городов:", error);
              options = [];
            }
            break;

          case "countryResponseForName":
            const countriesNameParams: FilterValueRequest = {
              limit,
              offset: currentOffset,
              country: undefined,
              search: currentSearchQuery,
            };

            try {
              const result = await fetchCountries(countriesNameParams);
              options = result.list.values.map((country) => ({
                value: country,
                label: country.value,
              }));
              hasMore = result.list.values.length === limit;
            } catch (error) {
              console.error("Ошибка загрузки стран:", error);
              options = [];
            }
            break;

          case "cityResponseForName":
            const selectedCountries = values["countryResponseForName"] as
              | FilterValueResponse[]
              | null;
            const citiesNameParams: FilterValueRequest = {
              limit,
              offset: currentOffset,
              country: selectedCountries?.map((el) => el.value),
              search: currentSearchQuery,
            };

            try {
              const result = await fetchCities(citiesNameParams);
              options = result.list.values.map((city) => ({
                value: city,
                label: city.value,
              }));
              hasMore = result.list.values.length === limit;
            } catch (error) {
              console.error("Ошибка загрузки городов:", error);
              options = [];
            }
            break;

          case "category":
            const selectedCountriesForCategory = values[
              "countryResponseForName"
            ] as FilterValueResponse[] | null;

            const categoryParams: FilterValueRequest = {
              limit,
              offset: currentOffset,
              country: selectedCountriesForCategory?.map((el) => el.value),
              search: currentSearchQuery,
            };

            try {
              const result = await fetchCategories(categoryParams);
              options = result.list.map((category) => ({
                value: category,
                label: truncateText(category.title, 32),
              }));
              hasMore = result.list.length === limit;
            } catch (error) {
              console.error("Ошибка загрузки категорий:", error);
              options = [];
            }
            break;

          case "subcategory":
            const selectedCategories = values["category"] as
              | CategoryResponse[]
              | null;

            const subcategoryParams: FilterValueRequest = {
              limit,
              offset: currentOffset,
              category_id: selectedCategories?.map((el) => el.id),
              search: currentSearchQuery,
            };

            try {
              const result = await fetchCatalogs(subcategoryParams);
              options = result.list.map((subcategory) => ({
                value: subcategory,
                label: truncateText(subcategory.title, 32),
              }));
              hasMore = result.list.length === limit;
            } catch (error) {
              console.error("Ошибка загрузки подкатегорий:", error);
              options = [];
            }
            break;

          case "platform":
            options = [
              { value: "ios", label: "iOS" },
              { value: "android", label: "Android" },
              { value: "web", label: "Web" },
            ];
            break;

          case "user_type":
            options = [
              { value: "buyer", label: "Покупатель" },
              { value: "seller", label: "Продавец" },
            ];
            break;

          case "buttonName":
            options = buttonNameOptions;
            break;

          case "timeOfDay":
            options = timeOfDayOptions;
            break;

          case "hint":
            options = hintOptions;
            break;

          case "screen":
          case "sourceScreen":
          case "targetScreen":
            options = screenOptions;
            break;

          case "actionType":
            options = actionOptions;
            break;

          case "contactType":
            options = contactOptions;
            break;

          case "filterName":
            const selectedSubcategories = values["subcategory"] as
              | CatalogResponse[]
              | null;

            const optionsParams: CatalogOptionsListRequest = {
              catalog_id: selectedSubcategories?.map((el) => el.id),
              form_name: "FILTER_DEFAULT",
              limit: limit,
              offset: currentOffset,
              ordering: [],
              parent_id: undefined,
              search: undefined,
            };

            try {
              const result = await fetchCatalogOptions(optionsParams);
              options = result.list.map((option) => ({
                value: option,
                label: truncateText(option.title, 32),
              }));
              hasMore = result.list.length === limit;
            } catch (error) {
              console.error("Ошибка загрузки опций:", error);
              options = [];
            }
            break;

          case "userType":
            options = userOptions;
            break;

          case "scoringUserType":
            options = scoringSegmentGroups.flatMap((group) =>
              group.items.map((item) => ({
                value: item.value,
                label: item.label,
              }))
            );
            break;

          default:
            options = [];
        }

        if (loadMore) {
          setLoadedOptions((prev) => ({
            ...prev,
            [filterKey]: [...(prev[filterKey] || []), ...options],
          }));
          filterOffsetsRef.current[filterKey] = currentOffset + limit;
        } else {
          setLoadedOptions((prev) => ({ ...prev, [filterKey]: options }));
          filterOffsetsRef.current[filterKey] = limit;
        }

        setFilterHasMore((prev) => ({ ...prev, [filterKey]: hasMore }));
      } catch (error) {
        console.error(`Failed to load options for ${filterKey}:`, error);
      } finally {
        if (loadMore) {
          setFilterLoadingMore((prev) => ({ ...prev, [filterKey]: false }));
        } else {
          setLoadingStates((prev) => ({ ...prev, [filterKey]: false }));
        }
      }
    },
    [
      // loadedOptions,
      // filterLoadingMore,
      // loadingStates,
      // filterOffsets,
      // filterHasMore,
      // searchQueries,
      values,
    ]
  );

  const onSearch = useCallback(
    (filterKey: FilterKey, searchQuery: string = "") => {
      console.log("onSearch", filterKey, searchQuery);
      setSearchQueries((prev) => ({ ...prev, [filterKey]: searchQuery }));
      loadFilterOptions(filterKey, false, true, searchQuery);
    },
    [loadFilterOptions]
  );

  const handleLoadMore = (filterKey: FilterKey) => {
    if (filterHasMore[filterKey] && !filterLoadingMore[filterKey]) {
      loadFilterOptions(filterKey, true);
    }
  };

  const resetFilterData = (filterKey: FilterKey) => {
    setLoadedOptions((prev) => ({ ...prev, [filterKey]: [] }));
    setFilterOffsets((prev) => ({ ...prev, [filterKey]: 0 }));
    setFilterHasMore((prev) => ({ ...prev, [filterKey]: false }));
    setFilterLoadingMore((prev) => ({ ...prev, [filterKey]: false }));
  };

  const getItems = (selectFilter: {
    key: FilterKey;
    options: FilterOption[];
  }) => {
    if (selectFilter.options.length > 0) {
      return selectFilter.options;
    }
    return loadedOptions[selectFilter.key] || [];
  };

  const getSelectedLabelForSubmenu = (
    selectFilter: { key: FilterKey; label: string; options: FilterOption[] },
    selectedValues: Record<FilterKey, Set<string>>
  ): string => {
    const currentSelectedValues =
      selectedValues[selectFilter.key] || new Set<string>([]);

    ////
    const items = getItems(selectFilter);

    const getItemValueString = (itemValue: any): string => {
      if (typeof itemValue === "string") return itemValue;

      // Для стран/городов (имеют поле value)
      if (itemValue?.value && typeof itemValue.value === "string") {
        return itemValue.value;
      }

      // Для категорий/подкатегорий (имеют поле id)
      if (itemValue?.id && typeof itemValue.id === "string") {
        return itemValue.id;
      }

      return "";
    };

    const selectedLabelsStr = Array.from(currentSelectedValues)
      .map((value) => {
        const item = items.find((item) => {
          const itemValue = getItemValueString(item.value);
          return itemValue === value;
        });
        return item?.label;
      })
      .filter(Boolean);

    if (currentSelectedValues.size === 0) {
      return "";
    }

    const selectedLabels = Array.from(currentSelectedValues);

    if (selectedLabels.length === 0) return "-";

    const formatScoringUserType = (labels: typeof selectedLabels) =>
      labels
        .map((segment) =>
          getScoringUserSegmentFilter(segment as ScoringUserSegment)
        )
        .join(", ");

    switch (selectFilter.key) {
      case "scoringUserType":
        return formatScoringUserType(selectedLabels);
      case "category":
      case "subcategory":
        return selectedLabels.length === 1
          ? selectedLabelsStr.join(", ")
          : `Выбрано: ${selectedLabels.length}`;
      default:
        return selectedLabels.length === 1
          ? selectedLabels[0] ?? "-"
          : `Выбрано: ${selectedLabels.length}`;
    }
  };

  const getGeographyLabel = (
    selectedValues: Record<FilterKey, Set<string>>
  ): string => {
    const countryLabel = getSelectedLabelForSubmenu(
      { key: "countryResponseForName", label: "Страна", options: [] },
      selectedValues
    );
    const cityLabel = getSelectedLabelForSubmenu(
      { key: "cityResponseForName", label: "Город", options: [] },
      selectedValues
    );
    if (!countryLabel && !cityLabel) return "";
    if (countryLabel && cityLabel) return `${countryLabel} / ${cityLabel}`;
    return countryLabel || cityLabel;
  };

  const getCategorySubcategoryLabel = (
    selectedValues: Record<FilterKey, Set<string>>
  ): string => {
    const categoryLabel = getSelectedLabelForSubmenu(
      { key: "category", label: "Раздел", options: [] },
      selectedValues
    );
    const subcategoryLabel = getSelectedLabelForSubmenu(
      { key: "subcategory", label: "Подраздел", options: [] },
      selectedValues
    );
    if (!categoryLabel && !subcategoryLabel) return "";
    if (categoryLabel && subcategoryLabel)
      return `${categoryLabel} / ${subcategoryLabel}`;
    return categoryLabel || subcategoryLabel;
  };

  const getFilterGroups = (filterKey: FilterKey): FilterGroup[] => {
    switch (filterKey) {
      case "scoringUserType":
        return scoringSegmentGroups;
      default:
        return [];
    }
  };

  return {
    loadedOptions,
    loadingStates,
    filterHasMore,
    filterLoadingMore,
    loadFilterOptions,
    onSearch,
    handleLoadMore,
    resetFilterData,
    buttonNameOptions,
    timeOfDayOptions,
    screenOptions,
    hintOptions,
    actionOptions,
    contactOptions,
    userOptions,
    getItems,
    getSelectedLabelForSubmenu,
    getGeographyLabel,
    getCategorySubcategoryLabel,
    getFilterGroups,
    scoringSegmentGroups,
  };
};
